/**
 * Opentok signals
 */
import beepAudio from 'assets/sounds/beep.mp3';
import useAuth from 'hooks/useAuth';
import useUser from 'hooks/useUser';
import { useSnackbar } from 'notistack';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import useStateRef from 'react-usestateref';
import { Layout } from './LayoutButton';

import pinIcon from 'assets/images/call/pin.svg';
import pinnedOffIcon from 'assets/images/call/pinned-off.svg';

let joinPromptShown = false;
let lastPinMoveDate = new Date();

type Props = {
  sessionRef?: any | null;
  floatingEmail?: string | null;
  screenNameRef?: RefObject<String | null>;
  isOwner?: boolean;
  publisherRef: any;
  subscribersRef: any;
  pinImageRef: RefObject<HTMLImageElement | null>;
  handleError: Function;
  isClient: boolean;
  isProvider: boolean;
};

export default function useSignals({
  sessionRef,
  floatingEmail,
  screenNameRef,
  isOwner,
  publisherRef,
  subscribersRef,
  pinImageRef,
  handleError,
  isClient,
  isProvider
}: Props) {
  const intl = useIntl();
  const [connectedUsers, setConnectedUsers, connectedUsersRef] = useStateRef({});
  const [isWaitingScreen, setWaitingScreen] = useState(true);
  const [publishVideo, setPublishVideo, publishVideoRef] = useStateRef(true);
  const [publishAudio, setPublishAudio, publishAudioRef] = useStateRef(true);
  const [allMuted, setAllMuted] = useState(false);
  const [isJoinPrompt, setJoinPrompt] = useState(false);
  const [isParticipantRemoved, setParticipantRemoved] = useState(false);
  const [sessionStarted, setSessionStarted, sessionStartedRef] = useStateRef(false);
  const [arrived, setArrived, arrivedRef] = useStateRef(false);
  const activeLayoutRef = useRef<Layout>('grid');
  const layoutBeforeRef = useRef<Layout>('grid');
  const pinnedStreamIdRef = useRef<String | null>(null);

  const [whiteboardOpen, setWhiteboardOpen, whiteboardOpenRef] = useStateRef(false);
  const [annotate, setAnnotate, annotateRef] = useStateRef(false);

  const [oneToOneChatEnabled, setOneToOneChatEnabled, oneToOneChatEnabledRef] = useStateRef(false);
  const [enableParticipantsToAnnotate, setEnableParticipantsToAnnotate, enableParticipantsToAnnotateRef] = useStateRef(false);

  // Camera/Microphone Permissions
  const [cameraGranted, setCameraGranted] = useState(true);

  const navigate = useNavigate();

  const { userId } = useAuth();
  const { user: loggedUser } = useUser(userId);
  const { enqueueSnackbar } = useSnackbar();

  const handleLayout = useCallback(() => {
    const subscribersGrid = document.getElementById('subscribersGrid')!;

    if (activeLayoutRef.current === 'grid' || activeLayoutRef.current === 'just-them' || activeLayoutRef.current === 'picture-in-picture') {
      const [subscribersGridCols, subscribersGridRows] = getColRowCount(subscribersGrid);

      if (subscribersGridCols || subscribersGridRows) {
        subscribersGrid.style.gridTemplateColumns = subscribersGridCols
          ? Array(subscribersGridCols)
              .fill(1)
              .map((c) => '1fr')
              .join(' ')
          : 'none';
        subscribersGrid.style.gridTemplateRows = subscribersGridRows
          ? Array(subscribersGridRows)
              .fill(1)
              .map((c) => '1fr')
              .join(' ')
          : 'none';
      }
    }
  }, []);

  const changeLayout = useCallback(
    (layout: Layout) => {
      const subscribersGrid = document.getElementById('subscribersGrid')!;
      const hiddenGrid = document.getElementById('hiddenGrid')!;

      const pipView = document.getElementById('pipView')!;

      const leftView = document.getElementById('leftView')!;
      const leftViewLeftPanel = leftView.querySelector('.leftPanel')!;
      const leftViewRightPanel = leftView.querySelector('.rightPanel')!;

      const justMe = document.getElementById('justMe')!;
      const pinned = document.getElementById('pinned')!;

      const applyPinIcon = (parent) => {
        const pinImage = pinImageRef.current!;

        const onPinClick = (streamId) => {
          if (!pinnedStreamIdRef.current) {
            pinnedStreamIdRef.current = streamId;
            layoutBeforeRef.current = activeLayoutRef.current;

            changeLayout('pinned');
          } else {
            pinnedStreamIdRef.current = null;

            changeLayout(layoutBeforeRef.current);
          }
        };

        parent.element.onmousemove = (e) => {
          const currentTime = new Date();
          if (currentTime.getTime() - lastPinMoveDate.getTime() < 100) return;

          parent.element.appendChild(pinImage);
          const centerX = parent.element.clientWidth / 2;
          const centerY = parent.element.clientHeight / 2;
          if ((e.offsetX < 50 && e.offsetY < 50) || (Math.abs(centerX - e.offsetX) < 100 && Math.abs(centerY - e.offsetY) < 80)) {
            pinImage.className = 'show';

            setTimeout(() => {
              if (pinImage?.className === 'show') {
                pinImage.className = 'hide';
              }
            }, 15000);
          } else {
            pinImage.className = 'hide';
          }
          if (!pinnedStreamIdRef.current) {
            pinImage.title = intl.formatMessage({ defaultMessage: 'Focus this participant' });
            pinImage.src = pinIcon;
          } else {
            pinImage.title = intl.formatMessage({ defaultMessage: 'Unfocus this participant' });
            pinImage.src = pinnedOffIcon;
          }

          pinImage.onclick = () => {
            setTimeout(() => {
              onPinClick(parent?.stream?.streamId);
            }, 500);
          };

          lastPinMoveDate = currentTime;
        };
      };

      activeLayoutRef.current = layout;
      if (layout === 'pinned' && pinnedStreamIdRef.current === null) {
        layoutBeforeRef.current = 'grid';
        activeLayoutRef.current = 'grid';
      }

      const screenElement = subscribersRef.current.findLast(
        (subscriber) => subscriber?.stream?.videoType === 'screen' || subscriber?.stream?.name === 'screen'
      );
      if (whiteboardOpenRef.current && screenElement) {
        setAnnotate(true);
      } else {
        setAnnotate(false);
      }

      switch (activeLayoutRef.current) {
        case 'pinned':
          pipView.style.display = 'none';
          subscribersGrid.style.display = 'none';
          leftView.style.display = 'none';
          justMe.style.display = 'none';
          pinned.style.display = 'block';

          const allChildren = [publisherRef.current];
          subscribersRef.current.forEach((subscriber) => {
            allChildren.push(subscriber);
          });

          const toBePinnedElement = allChildren.find((child) => child?.stream?.streamId === pinnedStreamIdRef.current);
          if (toBePinnedElement) {
            allChildren.forEach((child) => {
              if (child?.stream?.streamId !== pinnedStreamIdRef.current) {
                hiddenGrid.appendChild(child.element);
              } else {
                pinned.appendChild(child.element);
              }
            });
          }

          break;
        case 'grid':
          pipView.style.display = 'none';
          subscribersGrid.style.display = 'grid';
          leftView.style.display = 'none';
          justMe.style.display = 'none';
          pinned.style.display = 'none';

          subscribersGrid.appendChild(publisherRef.current.element);
          applyPinIcon(publisherRef.current);
          subscribersRef.current.forEach((subscriber) => {
            subscribersGrid.appendChild(subscriber.element);

            applyPinIcon(subscriber);
          });

          handleLayout();
          break;
        case 'just-them':
          pipView.style.display = 'none';
          subscribersGrid.style.display = 'grid';
          leftView.style.display = 'none';
          justMe.style.display = 'none';
          pinned.style.display = 'none';

          hiddenGrid.appendChild(publisherRef.current.element);
          subscribersRef.current.forEach((subscriber) => {
            subscribersGrid.appendChild(subscriber.element);
            applyPinIcon(subscriber);
          });
          handleLayout();
          break;
        case 'picture-in-picture':
          pipView.style.display = 'flex';
          subscribersGrid.style.display = 'grid';
          leftView.style.display = 'none';
          justMe.style.display = 'none';
          pinned.style.display = 'none';

          pipView.appendChild(publisherRef.current.element);
          subscribersRef.current.forEach((subscriber) => {
            subscribersGrid.appendChild(subscriber.element);
          });

          handleLayout();
          break;
        case 'just-me':
          pipView.style.display = 'none';
          subscribersGrid.style.display = 'none';
          leftView.style.display = 'none';
          justMe.style.display = 'flex';
          pinned.style.display = 'none';

          justMe.appendChild(publisherRef.current.element);
          handleLayout();
          break;
        case 'left-sidebar':
          pipView.style.display = 'none';
          subscribersGrid.style.display = 'none';
          leftView.style.display = 'grid';
          justMe.style.display = 'none';
          pinned.style.display = 'none';

          let rightViewElement;

          if (whiteboardOpenRef.current && !screenElement) {
            rightViewElement = null;
          } else {
            rightViewElement = screenElement;
            if (!rightViewElement && subscribersRef.current.length) {
              rightViewElement = subscribersRef.current[subscribersRef.current.length - 1];
            }
          }

          for (let i = subscribersRef.current.length - 1; i >= 0; i--) {
            leftViewLeftPanel.appendChild(subscribersRef.current[i].element);
          }
          leftViewLeftPanel.appendChild(publisherRef.current.element);
          if (rightViewElement) {
            leftViewRightPanel.appendChild(rightViewElement.element);
          }

          applyPinIcon(publisherRef.current);
          subscribersRef.current.forEach((subscriber) => {
            applyPinIcon(subscriber);
          });

          handleLayout();
          break;
      }
    },
    [handleLayout, publisherRef, subscribersRef, whiteboardOpenRef, intl, pinImageRef, setAnnotate]
  );

  /**
   * Send signal to all with user settings
   */
  const emitPresenceToAll = useCallback(() => {
    if (!sessionRef.current?.connection || !sessionStartedRef.current || !publisherRef.current) {
      return;
    }

    if (sessionStartedRef.current && arrivedRef.current) {
      publisherRef.current.publishVideo(publishVideoRef.current);
      publisherRef.current.publishAudio(publishAudioRef.current);
    }

    sessionRef.current?.signal({
      type: 'userConnected',
      data: JSON.stringify({
        connectionId: sessionRef.current?.connection?.connectionId,
        userId,
        floatingEmail,
        publishVideo: publishVideoRef.current,
        publishAudio: publishAudioRef.current,
        role: loggedUser?.role ?? 'CLIENT',
        screenName: screenNameRef?.current ?? '',
        image: loggedUser?.imageSmall,
        oneToOneChatEnabled: oneToOneChatEnabledRef.current,
        enableParticipantsToAnnotate: enableParticipantsToAnnotateRef.current,
        isOwner,
        isClient,
        isProvider
      })
    });
  }, [
    sessionRef,
    sessionStartedRef,
    publisherRef,
    arrivedRef,
    userId,
    floatingEmail,
    publishVideoRef,
    publishAudioRef,
    loggedUser?.role,
    loggedUser?.imageSmall,
    screenNameRef,
    oneToOneChatEnabledRef,
    enableParticipantsToAnnotateRef,
    isOwner,
    isClient,
    isProvider
  ]);

  const emitOpenWhiteboard = useCallback(
    (annotate = false) => {
      sessionRef.current?.signal({
        type: !annotate ? 'openWhiteboard' : 'annotate',
        data: JSON.stringify({
          connectionId: sessionRef.current?.connection?.connectionId
        })
      });
    },
    [sessionRef]
  );

  useEffect(() => {
    const session = sessionRef.current;

    return () => {
      console.log('>>>Destroying');
      session?.off(
        'streamCreated streamDestroyed connectionDestroyed signal:userConnected signal:muteCam signal:mute signal:muteAll signal:unmuteAll signal:removeParticipant signal:annotate'
      );
    };
  }, [sessionRef]);

  useEffect(() => {
    if (screenNameRef?.current) {
      emitPresenceToAll();
    }
  }, [sessionStarted, arrived, emitPresenceToAll, screenNameRef, publishVideo, publishAudio]);

  useEffect(() => {
    if (!cameraGranted) {
      setTimeout(() => {
        setPublishVideo(false);
        emitPresenceToAll();
      }, 800);
    }
  }, [cameraGranted, setPublishVideo, emitPresenceToAll]);

  /**
   * Mute/unmute all
   */
  const setMuteAll = useCallback(
    (value) => {
      setAllMuted(value);

      sessionRef.current?.signal({
        type: value ? 'muteAll' : 'unmuteAll'
      });
    },
    [sessionRef]
  );

  /**
   * Change oneToOneChatEnabled and send signal to all
   */
  const setOneToOneChatEnabledAndSignal = useCallback(
    (value) => {
      setOneToOneChatEnabled(value);
      emitPresenceToAll();
    },
    [setOneToOneChatEnabled, emitPresenceToAll]
  );
  /**
   * Change enableParticipantsToAnnotateVar and send signal to all
   */
  const setEnableParticipantsToAnnotateAndSignal = useCallback(
    (value) => {
      setEnableParticipantsToAnnotate(value);
      emitPresenceToAll();
    },
    [setEnableParticipantsToAnnotate, emitPresenceToAll]
  );

  /**
   * Add user to connectedUsers
   */
  const addConnectedUser = useCallback(
    (user) => {
      const userExisted = connectedUsersRef.current[user?.userId ?? user?.floatingEmail];
      if (user.image) {
        const subscriber = subscribersRef.current?.find((sub) => sub?.stream?.connection?.id === user?.connectionId);
        subscriber?.setStyle('backgroundImageURI', user.image);
      }

      connectedUsersRef.current[user?.userId ?? user?.floatingEmail] = user;
      setConnectedUsers({ ...connectedUsersRef.current });

      // Remove waiting screen, pause waiting music, show alert of mic and camera being used

      const audio = new Audio(beepAudio);
      if (user?.isProvider && (loggedUser?.isClient || !loggedUser?.role)) {
        setArrived(true);

        if (sessionStartedRef.current) {
          publisherRef.current.publishVideo(publishVideoRef.current);
          publisherRef.current.publishAudio(publishAudioRef.current);
        }

        if (!joinPromptShown && isWaitingScreen) {
          setJoinPrompt(true);
          joinPromptShown = true;
        }
        setWaitingScreen(false);

        if (!userExisted) {
          setTimeout(() => {
            audio.play();
            enqueueSnackbar(
              intl.locale === 'fr' ? `${user.screenName} (Fournisseur) est arrivé` : `${user.screenName} (Provider) has arrived`,
              { variant: 'success' }
            );
          }, 1500);
        }
      } else if ((user?.isClient || user?.isProvider || !user?.role) && loggedUser?.isProvider && user.userId !== loggedUser.id) {
        setArrived(true);

        if (sessionStartedRef.current) {
          publisherRef.current.publishVideo(publishVideoRef.current);
          publisherRef.current.publishAudio(publishAudioRef.current);
        }

        if (!joinPromptShown && isWaitingScreen) {
          setJoinPrompt(true);
          joinPromptShown = true;
        }
        setWaitingScreen(false);

        if (!userExisted) {
          setTimeout(() => {
            audio.play();
            if (user?.isProvider) {
              enqueueSnackbar(
                intl.locale === 'fr' ? `${user.screenName} (Fournisseur) est arrivé` : `${user.screenName} (Provider) has arrived`,
                { variant: 'success' }
              );
            } else {
              enqueueSnackbar(intl.locale === 'fr' ? `${user.screenName} (Client) est arrivé` : `${user.screenName} (Client) has arrived`, {
                variant: 'success'
              });
            }
          }, 1500);
        }
      }
    },
    [
      connectedUsersRef,
      setConnectedUsers,
      loggedUser,
      subscribersRef,
      setArrived,
      sessionStartedRef,
      isWaitingScreen,
      publisherRef,
      publishVideoRef,
      publishAudioRef,
      enqueueSnackbar,
      intl
    ]
  );

  /**
   * Remove user to connectedUsers
   */
  const removeConnectedUser = useCallback(
    (connectionId) => {
      const users = Object.values(connectedUsersRef.current);
      const foundUser: any = users.find((obj: any) => obj?.connectionId === connectionId)!;

      if (!foundUser) {
        return;
      }

      const audio = new Audio(beepAudio);
      audio.play();

      if (arrivedRef?.current) {
        if (foundUser?.isProvider) {
          enqueueSnackbar(
            intl.locale === 'fr' ? `${foundUser.screenName} (Fournisseur) a quitté` : `${foundUser.screenName} (Provider) has left`
          );
        } else {
          enqueueSnackbar(
            intl.locale === 'fr' ? `${foundUser.screenName} (Client) est parti` : `${foundUser.screenName} (Client) has left`
          );
        }
      }
      delete connectedUsersRef.current[foundUser?.userId ?? foundUser?.floatingEmail];

      setConnectedUsers({ ...connectedUsersRef.current });
    },
    [connectedUsersRef, arrivedRef, enqueueSnackbar, setConnectedUsers, intl]
  );

  const isCameraAvailable = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      return true;
    } catch (error: any) {
      return false;
    }
  };

  const setupSignals = useCallback(
    (token: string) => {
      const session = sessionRef.current!;

      session.on('streamCreated', (event: any) => {
        const storedToken = localStorage.getItem('token');
        if (storedToken !== token) {
          console.log('Token mismatch');
          window.location.replace('/');
        }

        const subscribersDiv = document.getElementById('subscribersGrid')!;

        const subscriberExists = subscribersRef.current.find((subscriber) => subscriber?.streamId === event?.stream?.streamId);
        if (subscriberExists) {
          return;
        }

        const subscriber = session.subscribe(
          event.stream,
          subscribersDiv,
          {
            insertMode: 'append',
            width: '100%',
            height: '100%',
            showControls: true,
            style: { buttonDisplayMode: 'off' }
          },
          handleError
        );

        subscribersRef.current.push(subscriber);
        if (event?.stream?.videoType === 'screen' || event?.stream?.name === 'screen') {
          pinnedStreamIdRef.current = null;
          layoutBeforeRef.current = activeLayoutRef.current;
          changeLayout('left-sidebar');
        } else {
          changeLayout(activeLayoutRef.current);
        }

        emitPresenceToAll();
      });

      session.on('streamDestroyed', (event: any) => {
        if (pinnedStreamIdRef.current === event.stream.streamId) {
          pinnedStreamIdRef.current = null;
        }

        subscribersRef.current = subscribersRef.current.filter((subscriber) => subscriber?.streamId !== event?.stream?.streamId);

        setTimeout(() => {
          changeLayout(
            event?.stream?.videoType === 'screen' || event?.stream?.name === 'screen' ? layoutBeforeRef.current : activeLayoutRef.current
          );
        }, 500);
      });

      session.on('connectionDestroyed', (event: any) => {
        removeConnectedUser(event.connection.id);
      });

      // User presence signal
      session.on('signal:userConnected', (event: any) => {
        const user = JSON.parse(event?.data);
        addConnectedUser(user);

        if (user.isProvider && 'oneToOneChatEnabled' in user) {
          setOneToOneChatEnabled(user.oneToOneChatEnabled);
          setEnableParticipantsToAnnotate(user.enableParticipantsToAnnotate);
        }
      });

      // Mute camera
      session.on('signal:muteCam', async (event: any) => {
        const data = JSON.parse(event?.data);

        if ((data.id && data.id === userId) || data.email === floatingEmail) {
          if (!(await isCameraAvailable())) {
            setPublishVideo(false);
            return;
          }
          setPublishVideo(data.value);
        }
      });

      // Mute audio
      session.on('signal:mute', (event: any) => {
        const data = JSON.parse(event?.data);

        if ((data.id && data.id === userId) || data.email === floatingEmail) {
          setPublishAudio(data.value);
        }
      });

      // Mute everyone's audio
      session.on('signal:muteAll', (event: any) => {
        if (!isOwner) {
          setPublishAudio(false);
        }
      });

      // Unmute everyone's audio
      session.on('signal:unmuteAll', (event: any) => {
        if (!isOwner) {
          setPublishAudio(true);
        }
      });

      // Participant Removed
      session.on('signal:removeParticipant', (event: any) => {
        const { id, email } = JSON.parse(event.data);
        if ((id && id === userId) || email === floatingEmail) {
          setParticipantRemoved(true);
          setTimeout(() => {
            session.disconnect();
            navigate('/appointments');
          }, 2000);
        }
      });

      session.on('signal:annotate', (event: any) => {
        activeLayoutRef.current = 'left-sidebar';
        pinnedStreamIdRef.current = null;
        setAnnotate(true);

        layoutBeforeRef.current = activeLayoutRef.current;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [floatingEmail, userId]
  );

  if (!sessionRef.current || !(floatingEmail || loggedUser)) {
    return {};
  }

  return {
    changeLayout,
    connectedUsers,
    isWaitingScreen,
    allMuted,
    setMuteAll,
    publishVideo,
    publishAudio,
    setPublishAudio,
    setPublishVideo,
    isJoinPrompt,
    setJoinPrompt,
    isParticipantRemoved,
    sessionStarted,
    setSessionStarted,
    arrived,
    emitOpenWhiteboard,
    activeLayoutRef,
    layoutBeforeRef,
    oneToOneChatEnabled,
    setOneToOneChatEnabledAndSignal,
    enableParticipantsToAnnotate,
    setEnableParticipantsToAnnotateAndSignal,
    cameraGranted,
    setCameraGranted,
    setupSignals,
    handleLayout,
    whiteboardOpen,
    setWhiteboardOpen,
    whiteboardOpenRef,
    annotate,
    setAnnotate,
    annotateRef,
    pinnedStreamIdRef,
    arrivedRef
  };
}

function getColRowCount(element) {
  const count = element.childElementCount;
  if (!count) return [0, 0];

  let colNum = 1;
  let rowNum = 1;

  if (count <= 1) {
    colNum = 1;
    rowNum = 1;
  } else if (count <= 2) {
    colNum = 2;
    rowNum = 1;
  } else if (count <= 4) {
    colNum = 2;
    rowNum = 2;
  } else if (count <= 5) {
    colNum = 2;
    rowNum = 3;
  } else {
    colNum = Math.ceil(Math.sqrt(count));
    rowNum = Math.ceil(Math.sqrt(count));
  }
  return [colNum, rowNum];
}
